<template>
  <div class="dialog-overlay" v-if="show">
    <div class="dialog-container">
      <div class="dialog-header">
        <h3 class="dialog-title">Move Lessons</h3>
        <button @click="$emit('close')" class="close-button" title="Close">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="close-icon">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <div class="dialog-content">
        <div class="selected-count">
          <span class="count">{{ selectedLessons.length }}</span>
          <span class="text">lessons selected</span>
        </div>
        
        <div class="form-group">
          <label for="course" class="form-label">Move to Course</label>
          <select 
            id="course" 
            v-model="selectedCourse" 
            class="form-select"
            required
          >
            <option value="">Select a course</option>
            <option 
              v-for="course in courses" 
              :key="course.id" 
              :value="course.id"
            >
              {{ course.title }}
            </option>
          </select>
        </div>
        
        <div class="warning" v-if="selectedCourse">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="warning-icon">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <p class="warning-text">
            This will move all selected lessons to the chosen course. This action cannot be undone.
          </p>
        </div>
      </div>
      
      <div class="dialog-footer">
        <button @click="$emit('close')" class="cancel-button">
          Cancel
        </button>
        <button 
          @click="confirmMove" 
          class="move-button"
          :disabled="!selectedCourse"
        >
          Move Lessons
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LessonsBulkMoveDialog',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    selectedLessons: {
      type: Array,
      default: () => []
    },
    courses: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedCourse: ''
    }
  },
  methods: {
    confirmMove() {
      if (this.selectedCourse) {
        this.$emit('move', this.selectedCourse);
      }
    }
  }
}
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.dialog-container {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.close-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: #e2e8f0;
}

.close-icon {
  width: 16px;
  height: 16px;
}

.dialog-content {
  padding: 1.5rem;
  overflow-y: auto;
}

.selected-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 6px;
}

.count {
  font-size: 1.125rem;
  font-weight: 600;
  color: #3b82f6;
}

.text {
  font-size: 0.875rem;
  color: #64748b;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
}

.form-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
  transition: all 0.2s ease;
}

.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.warning {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #fef3c7;
  border-radius: 6px;
}

.warning-icon {
  width: 20px;
  height: 20px;
  color: #d97706;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.warning-text {
  font-size: 0.875rem;
  color: #92400e;
  margin: 0;
  line-height: 1.5;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  margin-top: auto;
}

.cancel-button {
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-button:hover {
  background: #f8fafc;
}

.move-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  background: #3b82f6;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.move-button:hover:not(:disabled) {
  background: #2563eb;
}

.move-button:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .dialog-container {
    margin: 1rem;
  }
}
</style> 